// Gantt timeline. Offset is days from today (toolbar shifters
// mutate); zoom is day/week/month; showSpools toggles the per-spool
// sub-bars when a spool has both cutStart and weldDone.
import { useState } from "react";
import type {
  GanttZoom,
  JobDto,
} from "../../../../../../shared/types/production-schedule";
import { GanttHeader } from "../components/GanttHeader";
import { GanttRow } from "../components/GanttRow";
import { makeScale } from "../lib/ganttScale";

interface Props {
  jobs: JobDto[];
  onOpenJob(id: number): void;
}

const ZOOMS: { id: GanttZoom; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

export function GanttView({ jobs, onOpenJob }: Props) {
  const [offset, setOffset] = useState(0);
  const [zoom, setZoom] = useState<GanttZoom>("day");
  const [showSpools, setShowSpools] = useState(true);
  const [showStages, setShowStages] = useState(false);
  const scale = makeScale(zoom, offset);
  // A job is plottable when it has explicit start+due dates, or
  // when its spools/stages carry enough data for GanttRow to derive
  // a start boundary. A spool's dueDate alone isn't enough — without
  // a start, GanttRow returns null, so we'd render no row but still
  // suppress the "No jobs..." empty state.
  const renderable = jobs.filter((j) => {
    if (j.startDate && j.dueDate) return true;
    return j.spools.some((s) =>
      Boolean(s.cutStart && s.weldDone)
      || s.stages.some((st) => Boolean(st.startDate)),
    );
  });

  return (
    <div className="gantt-container">
      <div className="gantt-toolbar">
        <span className="gantt-toolbar-title">Production Gantt</span>
        <button type="button" className="gnav-btn" onClick={() => setOffset((o) => o - 14)}>← ←</button>
        <button type="button" className="gnav-btn" onClick={() => setOffset((o) => o - 7)}>← Wk</button>
        <button type="button" className="gnav-btn" onClick={() => setOffset(0)}>Today</button>
        <button type="button" className="gnav-btn" onClick={() => setOffset((o) => o + 7)}>Wk →</button>
        <button type="button" className="gnav-btn" onClick={() => setOffset((o) => o + 14)}>→ →</button>
        <div style={{ width: 1, height: 18, background: "var(--bsp-border)", margin: "0 3px" }} />
        {ZOOMS.map((z) => (
          <button
            key={z.id}
            type="button"
            className={`gz-btn ${zoom === z.id ? "active" : ""}`}
            onClick={() => setZoom(z.id)}
          >
            {z.label}
          </button>
        ))}
        <div style={{ width: 1, height: 18, background: "var(--bsp-border)", margin: "0 3px" }} />
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--bsp-fg-muted)", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input type="checkbox" checked={showSpools} onChange={(e) => setShowSpools(e.target.checked)} />
          Show spools
        </label>
        <label style={{ fontSize: 11, fontWeight: 600, color: "var(--bsp-fg-muted)", display: "flex", alignItems: "center", gap: 6, cursor: showSpools ? "pointer" : "not-allowed", opacity: showSpools ? 1 : 0.5 }}>
          <input
            type="checkbox"
            checked={showStages}
            disabled={!showSpools}
            onChange={(e) => setShowStages(e.target.checked)}
          />
          Show stages
        </label>
      </div>
      <div className="gantt-wrap">
        <div className="gantt-inner" style={{ minWidth: "max-content" }}>
          <GanttHeader scale={scale} />
          {renderable.length === 0 && (
            <div style={{ padding: 24, color: "var(--bsp-fg-muted)", textAlign: "center" }}>
              No jobs in the current window. Set start and due dates on jobs to plot them here.
            </div>
          )}
          {renderable.map((j) => (
            <GanttRow
              key={j.id}
              job={j}
              scale={scale}
              showSpools={showSpools}
              showStages={showStages}
              onOpen={onOpenJob}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
